import { HOUR, MINUTE } from './types.js';
export const DEFAULT_SESSION_CAPS = {
    maxCompactions: 2,
    maxAgeMs: 24 * HOUR,
    idleMs: 20 * MINUTE,
    warnFraction: 0.8,
};
/** Pure check of the safety caps. */
export function checkSessionCaps(s, now, caps = {}) {
    const c = { ...DEFAULT_SESSION_CAPS, ...caps };
    const reasons = [];
    const warnings = [];
    const age = now - s.startedAt;
    const busy = s.state === 'running' || s.state === 'compacting';
    const idleFor = busy ? 0 : now - s.lastActivityAt;
    if (s.compactions >= c.maxCompactions) {
        reasons.push({
            cap: 'compaction_cap',
            detail: `${s.compactions} compactions (cap ${c.maxCompactions}); hand off to a fresh session`,
        });
    }
    else if (s.compactions >= Math.max(1, Math.floor(c.maxCompactions * c.warnFraction))) {
        warnings.push(`compacted ${s.compactions}/${c.maxCompactions}; consider viko_handoff`);
    }
    if (age >= c.maxAgeMs) {
        reasons.push({
            cap: 'max_age',
            detail: `running ${Math.floor(age / HOUR)}h (cap ${c.maxAgeMs / HOUR}h)`,
        });
    }
    else if (age >= c.maxAgeMs * c.warnFraction) {
        warnings.push(`session age ${Math.floor(age / HOUR)}h of ${c.maxAgeMs / HOUR}h; consider viko_handoff`);
    }
    if (!busy && idleFor >= c.idleMs) {
        reasons.push({
            cap: 'idle',
            detail: `idle ${Math.floor(idleFor / MINUTE)} min (cap ${c.idleMs / MINUTE} min)`,
        });
    }
    const out = {
        action: reasons.length ? 'stop' : 'continue',
        reasons,
        resumable: true,
        warnings,
    };
    if (!reasons.length) {
        const candidates = [s.startedAt + c.maxAgeMs];
        if (!busy)
            candidates.push(s.lastActivityAt + c.idleMs);
        out.nextCheckAt = Math.min(...candidates);
    }
    return out;
}
const RUN_STATES = new Set([
    'running',
    'idle',
    'waiting',
    'compacting',
    'error',
]);
/** Fold a run's normalized events into the state the caps need. */
export function sessionStateFromEvents(events, init) {
    const s = init
        ? { ...init }
        : {
            startedAt: events[0]?.ts ?? 0,
            lastActivityAt: events[0]?.ts ?? 0,
            compactions: 0,
            state: 'idle',
        };
    for (const e of events) {
        // Rate-limit pushes arrive without activity (Codex sends them after model calls, Claude out of band).
        if (e.kind !== 'ratelimit')
            s.lastActivityAt = Math.max(s.lastActivityAt, e.ts);
        if (e.kind === 'run.start') {
            if (!init)
                s.startedAt = e.ts;
            s.state = 'running';
        }
        else if (e.kind === 'compaction') {
            s.compactions++;
        }
        else if (e.kind === 'status' && e.state && RUN_STATES.has(e.state)) {
            s.state = e.state;
        }
        else if (e.kind === 'turn.start') {
            s.state = 'running';
        }
        else if (e.kind === 'turn.end') {
            s.state = 'idle';
        }
    }
    return s;
}
//# sourceMappingURL=lifecycle.js.map